import { Box, Button, CircularProgress, Stack, Typography } from "@mui/joy";
import CustomModal from "components/common/CustomModal";
import React, { useEffect, useMemo, useRef, useState } from "react";
import useSafeAreaInsets from "hooks/useSafeAreaInsets";
import { sendMessage } from "utils/webview";
import useIsInappWebview from "hooks/useIsInappWebview";
import { theme } from "styles/theme";
import FloatingArea from "components/common/FloatingArea";
import { extractHexColor } from "../../../styles/hexOpacity";
import { useRecoilState } from "recoil";
import { paymentOrderAtom } from "../../../recoil/paymentOrderAtom";
import { useQuery } from "@tanstack/react-query";
import {
  PAYMENT_DOMAIN,
  PAYMENT_ORDER_DETAIL_ENDPOINT,
  paymentApi,
} from "api/paymentApi";
import { userAtom } from "../../../recoil/userAtom";
import CustomTypography from "../../common/CustomTypography";
import ContentTemplateV2 from "../content/ContentTemplateV2";
import { axiosInstance } from "../../../api";
import useMixpanelWeb from "../../../hooks/useMixpanelWeb";
import CheckIcon from "../../icons/CheckIcon";
import InfoIcon from "../../icons/InfoIcon";
import PlanUpgradeButton from "./elements/PlanUpgradeButton";
import useGoBack from "../../../hooks/useGoBack";
import BackButton from "components/common/BackButton";
import { useSearchParams } from "react-router-dom";
import {
  applyMultiPlanPrices,
  applyPaymentPageType,
  applyPaymentPageTypeSTags,
  applyPromotionPrices,
  getDedicatedPaymentBannerLabel,
  isAlertTag,
  PlanItem,
  PlanKey,
} from "./planTypes";

// feature.key 는 안정 식별자(INFO_CONTENTS / refundLabel 분기). text 는 카피로 자유 갱신.
// "payback" key 는 렌더 시 refundLabel(효과 보장 페이백 / 14일 내 전액 환불 보장) 로 동적 치환됨.
export const plans: PlanItem[] = [
  {
    key: "starter",
    name: "스타터",
    price: 295000,
    features: [
      {
        key: "coaching_starter",
        text: "최대 주 2회 코칭",
        included: true,
        info: true,
      },
      { key: "payback", text: "환불 페이백", included: true },
    ],
  },
  {
    key: "challenger",
    name: "챌린저",
    price: 395000,
    tag: "가장 인기 있는",
    features: [
      {
        key: "coaching_challenger",
        text: "2배 더 많은 코칭 (스타터 대비)",
        included: true,
        info: true,
      },
      {
        key: "regular_check",
        text: "정기 검사",
        included: true,
        info: true,
      },
      { key: "payback", text: "환불 페이백", included: true },
    ],
  },
  {
    key: "master",
    name: "마스터",
    price: 465000,
    tag: "가장 밀도 있는",
    features: [
      {
        key: "coaching_master",
        text: "2배 더 많은 코칭 (챌린저 대비)",
        included: true,
        info: true,
      },
      {
        key: "coaching_master_speed",
        text: "5시간 더 빠른 코칭 (챌린저 대비)",
        included: true,
        info: true,
      },
      {
        key: "regular_check",
        text: "정기 검사",
        included: true,
        info: true,
      },
      { key: "payback", text: "환불 페이백", included: true },
    ],
  },
];

const formatCurrency = (n: number) => n.toLocaleString("ko-KR");

export default function PlanUpgradeScreen() {
  const floatingAreaRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();

  const from = searchParams.get("from");
  const isInappWebview = useIsInappWebview();
  const goBack = useGoBack();

  const handleBack = () => {
    try {
      sessionStorage.removeItem(SESSION_SELECTED_PLAN_KEY);
    } catch {}
    goBack();
  };

  const paddingX = 4.5;

  const [user, setUser] = useRecoilState(userAtom);

  const [paymentOrderState, setPaymentOrderState] =
    useRecoilState(paymentOrderAtom);

  const isFirstOrder = paymentOrderState?.isFirst;
  const fullWidth = isFirstOrder ? true : !!isInappWebview;

  const isPaybackPolicy =
    user?.refundPolicyType !== "c" && user?.refundPolicyType !== "b";
  const refundLabel = isPaybackPolicy
    ? "효과 보장 페이백"
    : "14일 내 전액 환불 보장";

  const currentPlan = user?.plan || "challenger";

  const dedicatedBannerLabel = getDedicatedPaymentBannerLabel(user?.promotion);

  // ajk 등 다중 플랜 가격 오버라이드 + 제휴 프로모션 전용가(우선) + paymentPageType "p" 마스터 feature 추가
  const effectivePlans = useMemo(
    () =>
      applyPaymentPageTypeSTags(
        applyPaymentPageType(
          applyPromotionPrices(
            applyMultiPlanPrices(plans, user?.pricingModel),
            user?.promotion
          ),
          user?.paymentPageType
        ),
        user?.paymentPageType
      ),
    [user?.pricingModel, user?.promotion, user?.paymentPageType]
  );

  // 상위 플랜만 노출 (업그레이드만 가능)
  const planOrder: PlanKey[] = ["starter", "challenger", "master"];
  const availablePlans = useMemo(() => {
    const currentIndex = currentPlan
      ? planOrder.indexOf(currentPlan as PlanKey)
      : -1;
    // 현재 이용 중 플랜은 그대로 노출하고, 그보다 낮은 플랜만 숨김
    return effectivePlans.filter(
      (p) => planOrder.indexOf(p.key) >= currentIndex
    );
  }, [currentPlan, effectivePlans]);

  useQuery(
    [PAYMENT_DOMAIN, PAYMENT_ORDER_DETAIL_ENDPOINT, user?.userId],
    () => paymentApi.getOrderDetail(),
    {
      enabled:
        !!axiosInstance.defaults.headers["X-Auth-Token"] && !!user?.accessToken,
      onSuccess: (data) => {
        if (data && data.result) {
          setPaymentOrderState(data.result);
        }
      },
    }
  );

  const setMixPanel = useMixpanelWeb({
    eventName: "플랜 업그레이드 진입",
    params: { from: from === "home" ? "home" : "payment-history" },
  });

  function useEffectOnce(callback: () => void) {
    const ref = useRef(false);

    useEffect(() => {
      if (ref.current) return;
      ref.current = true;

      if (typeof callback === "function") {
        callback();
      }
    }, []);
  }

  useEffectOnce(() => {
    setMixPanel();
  });

  const hasSentSafeAreaColorRef = useRef(false);

  useEffect(() => {
    if (hasSentSafeAreaColorRef.current) return;
    if (!(isInappWebview && paymentOrderState)) return;

    const topBackground = extractHexColor(theme.vars.palette.Background.Level3);
    const bottomBackground = extractHexColor(
      theme.vars.palette.Background.Level1
    );

    if (topBackground && bottomBackground) {
      sendMessage(
        JSON.stringify({
          event: "set_safearea_color",
          params: {
            topColor: `${topBackground}`,
            bottomColor: `${bottomBackground}`,
          },
        })
      );
      hasSentSafeAreaColorRef.current = true;
    }
  }, [isInappWebview, paymentOrderState]);

  const { bottom: safeBottom } = useSafeAreaInsets();
  const [containerHeight, setContainerHeight] = useState<number>(
    window.innerHeight - safeBottom
  );
  const [floatingAreaHeight, setFloatingAreaHeight] = useState<number>(0);

  useEffect(() => {
    setTimeout(() => {
      setFloatingAreaHeight(floatingAreaRef.current?.offsetHeight || 0);
    }, 100);
  }, [floatingAreaRef.current]);

  /* -----------------------------
    신규: 선택된 플랜 상태/헬퍼
  ------------------------------ */
  const SESSION_SELECTED_PLAN_KEY = "payment.selectedPlan.planUpgradeScreen";
  const [selectedPlan, setSelectedPlan] = useState<PlanKey>(() => {
    try {
      if (typeof window !== "undefined") {
        const saved = sessionStorage.getItem(SESSION_SELECTED_PLAN_KEY);
        if (
          saved &&
          (saved === "starter" || saved === "challenger" || saved === "master")
        ) {
          return saved as PlanKey;
        }
      }
    } catch {}
    // 기본값: 화면 진입 시 현재 플랜의 한 단계 위를 선택
    const order: PlanKey[] = ["starter", "challenger", "master"];
    const idx = currentPlan ? order.indexOf(currentPlan as PlanKey) : -1;
    if (idx >= 0) {
      const next = order[Math.min(idx + 1, order.length - 1)];
      return next;
    }
    return "challenger";
  });
  useEffect(() => {
    try {
      if (typeof window !== "undefined") {
        sessionStorage.setItem(SESSION_SELECTED_PLAN_KEY, selectedPlan);
      }
    } catch {}
  }, [selectedPlan]);

  // 현재 플랜이 늦게 로드되는 경우에도, 저장된 값이 없다면 기본 선택을 한 단계 위로 보정
  useEffect(() => {
    try {
      if (typeof window !== "undefined") {
        const saved = sessionStorage.getItem(SESSION_SELECTED_PLAN_KEY);
        const isValidSaved =
          saved === "starter" || saved === "challenger" || saved === "master";
        if (isValidSaved) return; // 사용자가 이전에 선택한 값이 있으면 존중
      }
    } catch {}
    const order: PlanKey[] = ["starter", "challenger", "master"];
    const idx = currentPlan ? order.indexOf(currentPlan as PlanKey) : -1;
    if (idx >= 0) {
      const next = order[Math.min(idx + 1, order.length - 1)];
      if (selectedPlan !== next) setSelectedPlan(next);
    }
  }, [currentPlan]);

  // 보정: 선택된 플랜이 표시되지 않거나(숨김) 또는 비활성화(현재 플랜)라면,
  // 선택 가능한 것들 중 가장 아래 플랜을 자동 선택
  useEffect(() => {
    if (availablePlans.length === 0) return;

    const isVisible = availablePlans.some((p) => p.key === selectedPlan);
    const isDisabled = selectedPlan === (currentPlan as PlanKey);

    if (!isVisible || isDisabled) {
      const selectable = availablePlans.filter(
        (p) => p.key !== (currentPlan as PlanKey)
      );
      if (selectable.length > 0) {
        setSelectedPlan(selectable[selectable.length - 1].key);
      }
    }
  }, [availablePlans, currentPlan, selectedPlan]);
  const selectedPlanObj = effectivePlans.find((p) => p.key === selectedPlan)!;
  const currentPlanObj = effectivePlans.find(
    (p) => p.key === (currentPlan as PlanKey)
  );
  const priceDiff = Math.max(
    0,
    selectedPlanObj.price - (currentPlanObj?.price ?? 0)
  );

  const planTitleRef = useRef<HTMLDivElement>(null); // title anchor

  type InfoModalData = { title: string; subtitle: string; action: string };

  // feature.key 기준. text 카피가 바뀌어도 여기 영향 없음.
  const INFO_CONTENTS: Record<string, InfoModalData> = {
    coaching_starter: {
      title: "기본 코칭",
      subtitle:
        "일주일에 최대 2회, 코치의 피드백과 함께 맞춤 활동지가 제공됩니다. 프로그램 진도도 매주 최대 2회까지 나가게 됩니다.",
      action: "확인",
    },
    coaching_challenger: {
      title: "집중 코칭",
      subtitle:
        "하루 최대 1회, 코치의 피드백과 함께 맞춤 활동지가 제공됩니다. 프로그램 진도도 매일 최대 1회까지 나가게 됩니다.",
      action: "확인",
    },
    coaching_master: {
      title: "프리미엄 코칭",
      subtitle:
        "하루 최대 2회, 코치의 피드백과 함께 맞춤 활동지가 제공됩니다. 프로그램 진도도 매일 최대 2회까지 나가게 됩니다.",
      action: "확인",
    },
    coaching_master_speed: {
      title: "프리미엄 코칭",
      subtitle:
        "스타터와 챌린저 플랜 대비 평균 5시간 더 빨리 코칭을 받을 수 있습니다.",
      action: "확인",
    },
    lead_coach: {
      title: "수석 코치",
      subtitle:
        "오랜 기간 다양한 사례를 성공적으로 이끌어온 상위 10%의 수석 코치가 배정됩니다.",
      action: "확인",
    },
    regular_check: {
      title: "정기 검사",
      subtitle: "정신건강 주요 항목을 2주마다 정기적으로 측정합니다.",
      action: "확인",
    },
  };

  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [infoModal, setInfoModal] = useState<InfoModalData>({
    title: "",
    subtitle: "",
    action: "확인",
  });

  const handleOpenInfo = (feature: { key: string; text: string }) => {
    const next = INFO_CONTENTS[feature.key] || {
      title: feature.text,
      subtitle: "자세한 내용은 고객센터로 문의해주세요.",
      action: "확인",
    };
    setInfoModal(next);
    setInfoModalOpen(true);
  };

  // Show a loading indicator in the center when paymentOrderState is not ready
  if (!paymentOrderState) {
    return (
      <ContentTemplateV2
        fullBleed
        disableBottomSafeArea
        isWide
        setContainerHeight={setContainerHeight}
      >
        <Stack
          sx={{ width: "100%", minHeight: containerHeight }}
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress color="neutral" />
        </Stack>
      </ContentTemplateV2>
    );
  }

  return (
    <ContentTemplateV2
      fullBleed
      disableBottomSafeArea
      isWide
      setContainerHeight={setContainerHeight}
      backgroundColor={theme.vars.palette.Background.Level3}
    >
      {containerHeight > 0 && (
        <Stack
          sx={{
            // floating area(~100px) + footer↔floating 시각 여백.
            pb: "100px",
            backgroundColor: theme.vars.palette.Background.Level3,
          }}
        >
          {dedicatedBannerLabel && (
            <Box
              sx={{
                width: "100%",
                height: "42px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: "24px",
                background: theme.vars.palette.Surface.SoftHighlight,
                position: "sticky",
                top: 0, // 최상단 고정 (헤더보다 위)
                zIndex: 101, // 헤더 zIndex(100) 위
              }}
            >
              <CustomTypography
                variant="Caption"
                sx={{
                  color: theme.vars.palette.Content.Highlight,
                  fontWeight: 500,
                }}
              >
                {dedicatedBannerLabel}
              </CustomTypography>
            </Box>
          )}

          {/* 헤더 */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              width: "100%",
              px: "8px",
              position: "sticky",
              top: dedicatedBannerLabel ? "42px" : 0, // 전용 띠(42px) 아래로 헤더 이동
              zIndex: 100,
              height: "60px",
              backgroundColor: theme.vars.palette.Background.Level3,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <BackButton onBack={handleBack} />
              <Typography
                variant="Body"
                sx={{
                  px: 0,
                  color: theme.vars.palette.Content.Strong,
                  fontWeight: 700,
                }}
              >
                플랜 업그레이드
              </Typography>
            </Stack>
          </Stack>

          {/* 플랜 선택 */}
          <Stack
            justifyContent={isInappWebview ? "flex-start" : "center"}
            alignItems={"center"}
            sx={{
              width: "100%",
              maxWidth: "100%",
              mx: "auto",
              px: paddingX,
              pb: 15,
              pt: 1,
              ...(isInappWebview
                ? {}
                : {
                    minHeight: Math.max(
                      0,
                      containerHeight - floatingAreaHeight
                    ),
                  }),
            }}
          >
            <Stack
              sx={{
                width: "100%",
                maxWidth: isInappWebview ? "660px" : "1000px",
              }}
              spacing={5}
              alignItems="center"
            >
              <Box
                sx={{
                  width: "100%",
                  display: "grid",
                  gridTemplateColumns: isInappWebview
                    ? "1fr"
                    : availablePlans.length === 2
                      ? "repeat(2, minmax(0, 1fr))"
                      : "repeat(3, minmax(0, 1fr))",
                  gap: "12px",
                  ...(isInappWebview || availablePlans.length !== 2
                    ? {}
                    : {
                        maxWidth: "720px",
                        mx: "auto",
                        justifyContent: "center",
                      }),
                }}
              >
                {availablePlans.map((plan) => {
                  const selected = selectedPlan === plan.key;
                  const isCurrentPlan = currentPlan === plan.key;
                  return (
                    <Box
                      key={plan.key}
                      sx={{
                        borderRadius: "16px",
                        backgroundColor: theme.vars.palette.Surface.Forward,
                        px: "24px",
                        py: "28px",
                      }}
                    >
                      <Stack spacing={"20px"}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="flex-start"
                        >
                          <Stack spacing={0.5} sx={{ width: "100%" }}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              sx={{ width: "100%", pl: 0.5 }}
                            >
                              <CustomTypography
                                variant="Body"
                                sx={{
                                  color: theme.vars.palette.Content.Highlight,
                                  fontWeight: 700,
                                }}
                              >
                                {plan.name}
                              </CustomTypography>
                              {plan.tag && (
                                <Box
                                  sx={{
                                    height: "20px",
                                    borderRadius: "4px",
                                    px: "4px",
                                    display: "flex",
                                    alignItems: "center",
                                    backgroundColor: isAlertTag(plan.tag)
                                      ? theme.vars.palette.Surface
                                          .SoftHighlightWithAlert
                                      : theme.vars.palette.Surface
                                          .SoftHighlight,
                                  }}
                                >
                                  <Typography
                                    variant="Footnote"
                                    sx={{
                                      color: isAlertTag(plan.tag)
                                        ? theme.vars.palette.Content
                                            .HighlightWithAlert
                                        : theme.vars.palette.Content.Highlight,
                                      fontWeight: 700,
                                    }}
                                  >
                                    {plan.tag}
                                  </Typography>
                                </Box>
                              )}
                            </Stack>
                            <CustomTypography
                              variant="H3"
                              sx={{
                                color: theme.vars.palette.Content.Strong,
                                fontWeight: 700,
                              }}
                            >
                              {`${formatCurrency(plan.price)}원`}
                              <CustomTypography
                                component="span"
                                variant="Body"
                                sx={{
                                  ml: 0.5,
                                  color: theme.vars.palette.Content.Medium,
                                  fontWeight: 500,
                                }}
                              >
                                {"/ 100일"}
                              </CustomTypography>
                            </CustomTypography>
                          </Stack>
                        </Stack>

                        {/* 선택 버튼 */}
                        <Button
                          fullWidth
                          size="md"
                          variant={
                            isCurrentPlan
                              ? "Soft"
                              : selected
                                ? "Outlined"
                                : "Soft"
                          }
                          disabled={isCurrentPlan}
                          onClick={() => {
                            if (!isCurrentPlan) setSelectedPlan(plan.key);
                          }}
                          sx={{ borderRadius: "10px", fontWeight: 700 }}
                        >
                          {isCurrentPlan ? (
                            "현재 이용 중"
                          ) : selected ? (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <CheckIcon ariaLabel="selected" />
                              <span>선택됨</span>
                            </span>
                          ) : (
                            "선택하기"
                          )}
                        </Button>

                        {/* 혜택 목록 — PaymentContentDefault isFirstOrder 카드 유닛과 동일 */}
                        <Stack spacing={1}>
                          {plan.features.map((f, idx) => (
                            <Stack
                              key={`${plan.key}-${idx}`}
                              direction="row"
                              alignItems="flex-start"
                              justifyContent="space-between"
                              sx={{ width: "100%" }}
                            >
                              <Stack
                                direction="row"
                                alignItems="flex-start"
                                spacing={0.5}
                              >
                                <CheckIcon
                                  ariaLabel="included"
                                  style={{ marginTop: "2px" }}
                                />
                                <Typography
                                  variant="Body"
                                  sx={{
                                    px: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    color: theme.vars.palette.Content.Strong,
                                    fontWeight: 500,
                                  }}
                                >
                                  {f.key === "payback" ? refundLabel : f.text}
                                </Typography>
                              </Stack>
                              {f.info && (
                                <Box
                                  role="button"
                                  tabIndex={0}
                                  aria-label={`${f.text} 자세히 보기`}
                                  onClick={() => handleOpenInfo(f)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      handleOpenInfo(f);
                                    }
                                  }}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    cursor: "pointer",
                                  }}
                                >
                                  <InfoIcon ariaLabel="info" />
                                </Box>
                              )}
                            </Stack>
                          ))}
                        </Stack>
                      </Stack>
                    </Box>
                  );
                })}
              </Box>

              <CustomTypography
                variant={"Caption"}
                sx={{
                  color: theme.vars.palette.Content.Weak,
                  fontWeight: 500,
                  width: "100%",
                  textAlign: "center",
                }}
              >
                {
                  "플랜을 업그레이드해도 이용 만료일은 변경되지 않으며, 페이백/환불 정책도 동일하게 적용됩니다."
                }
              </CustomTypography>
            </Stack>
          </Stack>

          <Box>
            <FloatingArea
              fullWidth={fullWidth}
              ref={floatingAreaRef}
              paddingBottom={"12px"}
            >
              <Stack
                spacing={1.5}
                sx={{ width: "100%" }}
                alignItems="center"
                justifyContent={"space-between"}
              >
                {/* 선택된 플랜에 따른 문구 동기화 */}
                <CustomTypography
                  variant={"Caption"}
                  sx={{
                    p: 0,
                    fontWeight: 500,
                    color: theme.vars.palette.Content.Medium,
                    textAlign: "center",
                  }}
                >
                  {`차액 ${formatCurrency(priceDiff)}원 추가 결제`}
                </CustomTypography>

                <Stack
                  spacing={"16px"}
                  sx={{ width: "100%" }}
                  alignItems="center"
                  justifyContent={"space-between"}
                >
                  {paymentOrderState && currentPlan && selectedPlan && (
                    <PlanUpgradeButton
                      currentPlan={currentPlan!!}
                      nextPlan={selectedPlan}
                    />
                  )}
                </Stack>
              </Stack>
            </FloatingArea>
          </Box>
        </Stack>
      )}
      <CustomModal
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        title={infoModal.title}
        subtitle={infoModal.subtitle}
        actions={[
          {
            label: infoModal.action,
            onClick: () => setInfoModalOpen(false),
            variant: "Solid",
          },
        ]}
      />
    </ContentTemplateV2>
  );
}
